import React, { createContext, useCallback, useContext, useMemo, useState } from 'react'
import { converter } from 'culori';

export type ThemeButtonShape = 'rounded' | 'rect' | null
export type ThemeButtonVariant = 'outline' | null

interface HeyThemeContext {
  color_primary: string | null
  color_neutral: string | null
  color_content: string | null
  buttonShape: ThemeButtonShape
  buttonVariant: ThemeButtonVariant
  setTheme: (
    color_primary: string,
    color_neutral: string,
    color_content: string,
    font?: string | null,
    font_content?: string | null,
    fontMultiplier?: number | null,
    fontContentMultiplier?: number | null,
    buttonShape?: ThemeButtonShape,
    buttonVariant?: ThemeButtonVariant,
  ) => void
  style: any
}

export const HeyThemeContext = createContext<HeyThemeContext>({
  color_primary: null,
  color_neutral: null,
  color_content: null,
  buttonShape: null,
  buttonVariant: null,
  setTheme() {},
  style: null,
})

// Helper functions for color calculations
function prepareColor(color: string) {
  if (color[0] != '#') {
    color = '#' + color
  }

  if (color.toLowerCase() === '#000000') {
    color = '#000001'
  }

  if (color.toLowerCase() === '#ffffff') {
    color = '#FFFFFE'
  }

  return color
}

function culoriToString(color: any) {
  return `${color.l || 0} ${color.c || 0} ${color.h || 0}`
}

function calculateStyle(
  colorPrimary: string | null,
  colorNeutral: string | null,
  colorContent: string | null,
  font?: string | null,
  font_content?: string | null,
  fontMultiplier?: number | null,
  fontContentMultiplier?: number | null,
  buttonShape?: ThemeButtonShape,
  ) {
  if (!(colorPrimary  && colorNeutral && colorContent)) {
    return null
  }

  const oklch = converter('oklch')

  const primary = oklch(prepareColor(colorPrimary))!
  const neutral = oklch(prepareColor(colorNeutral))!
  const content = oklch(prepareColor(colorContent))!
  const neutralLight = neutral.l < 0.2 ? 0.2 : 0.95 * neutral.l

  const roundedBtn = buttonShape === 'rect' ? '--rounded-btn: 0;' : ''

  if (!font && !font_content) {
    return `
    :root {
      --p: ${culoriToString(primary)};
      --n: ${culoriToString(neutral)};
      --pc: ${culoriToString(neutral)};
      --s: ${culoriToString({ ...neutral, l: neutralLight })};
      --bc: ${culoriToString(content)};
      --b1: ${culoriToString({ ...neutral, l: neutralLight })};
      --a: ${culoriToString(content)};
      --ac: ${culoriToString(neutral)};
      ${roundedBtn}
    }
  `
  }

  let fontMultiplierString = ''
  if (fontMultiplier) {
    fontMultiplierString = `
        --theme-font-multiplier: ${fontMultiplier};
      `
  }

  if (fontContentMultiplier) {
    fontMultiplierString = `${fontMultiplierString} --theme-font-content-multiplier: ${fontContentMultiplier};`
  }

  return `
    :root {
      --p: ${culoriToString(primary)};
      --n: ${culoriToString(neutral)};
      --pc: ${culoriToString(neutral)};
      --s: ${culoriToString({ ...neutral, l: neutralLight })};
      --bc: ${culoriToString(content)};
      --b1: ${culoriToString({ ...neutral, l: neutralLight })};
      --a: ${culoriToString(content)};
      --ac: ${culoriToString(neutral)};
      ${roundedBtn}
      ${fontMultiplierString}
      --theme-font-family-heading: ${font};
      --theme-font-family: ${font_content};
    }
    `
}

// Constant theme provider (uses same context, but setTheme is a no-op)
interface HeyThemeConstantProviderProps {
  colorPrimary: string
  colorNeutral: string
  colorContent: string
  children: React.ReactNode
  font?: string,
  font_content?: string,
  fontMultiplier?: number
  fontContentMultiplier?: number
  buttonShape?: ThemeButtonShape
  buttonVariant?: ThemeButtonVariant
}

export function HeyThemeConstantProvider({
                                           colorPrimary, colorNeutral, colorContent, children,
                                           font,
                                           font_content,
                                           fontMultiplier,
                                           fontContentMultiplier,
                                           buttonShape: buttonShapeProp = null,
                                           buttonVariant: buttonVariantProp = null,
}: HeyThemeConstantProviderProps) {
  const style = useMemo(() => {
    return calculateStyle(colorPrimary, colorNeutral, colorContent, font, font_content, fontMultiplier, fontContentMultiplier, buttonShapeProp)
  }, [colorPrimary, colorNeutral, colorContent, font, font_content, fontMultiplier, fontContentMultiplier, buttonShapeProp])

  const value = useMemo(() => ({
    color_primary: colorPrimary,
    color_neutral: colorNeutral,
    color_content: colorContent,
    buttonShape: buttonShapeProp,
    buttonVariant: buttonVariantProp,
    setTheme: () => {},
    style,
    font,
    font_content,
    fontMultiplier,
    fontContentMultiplier,
  }), [colorPrimary, colorNeutral, colorContent, buttonShapeProp, buttonVariantProp, style, font, font_content, fontContentMultiplier, fontMultiplier])

  return <HeyThemeContext.Provider value={value}>
    {children}
  </HeyThemeContext.Provider>
}

export function HeyThemeProvider({ children }: { children: React.ReactNode }) {
  const [color_primary, setColor_primary] = useState<string | null>(null)
  const [color_neutral, setColor_neutral] = useState<string | null>(null)
  const [color_content, setColor_content] = useState<string | null>(null)
  const [buttonShape, setButtonShape] = useState<ThemeButtonShape>(null)
  const [buttonVariant, setButtonVariant] = useState<ThemeButtonVariant>(null)

  const [fontState, setFont] = useState<string | null>(null)
  const [font_contentState, setFontcontent] = useState<string | null>(null)
  const [fontMultiplierState, setFontMultiplierState] = useState<number | null>(null)
  const [fontContentMultiplier, setFontContentMultiplier] = useState<number | null>(null)


  const setTheme = useCallback((
    color_primary: string,
    color_neutral: string,
    color_content: string,
    font?: string | null,
    font_content?: string | null,
    fontMultiplier?: number | null,
    fontContentMultiplier?: number | null,
    buttonShape?: ThemeButtonShape,
    buttonVariant?: ThemeButtonVariant,
    ): void => {
    setColor_primary(color_primary)
    setColor_neutral(color_neutral)
    setColor_content(color_content)
    if (font !== undefined)
      setFont(font)
    if (font_content !== undefined)
      setFontcontent(font_content)
    if (fontMultiplier !== undefined)
      setFontMultiplierState(fontMultiplier)
    if (fontContentMultiplier !== undefined)
      setFontContentMultiplier(fontContentMultiplier)
    if (buttonShape !== undefined)
      setButtonShape(buttonShape)
    if (buttonVariant !== undefined)
      setButtonVariant(buttonVariant)
  }, [
    setColor_primary,
    setColor_neutral,
    setColor_content,
    setFont,
    setFontcontent,
    setFontMultiplierState,
    setFontContentMultiplier,
  ])

  const style = useMemo(() => {
    return calculateStyle(color_primary, color_neutral, color_content, fontState, font_contentState, fontMultiplierState, fontContentMultiplier, buttonShape)
  }, [color_primary,
    color_neutral,
    color_content,
    fontState,
    font_contentState,
    fontMultiplierState,
    fontContentMultiplier,
    buttonShape,]);

  return <HeyThemeContext.Provider value={{
    color_primary,
    color_neutral,
    color_content,
    buttonShape,
    buttonVariant,
    setTheme,
    style,
  }}>
    { children }
  </HeyThemeContext.Provider>
}

export function useHeyTheme() {
  return useContext(HeyThemeContext)
}
